import axios from 'axios';
import { toast } from 'react-toastify';

const axiosInstance = axios.create({
    baseURL:         process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1',
    withCredentials: true,   // needed for refreshToken cookie
    timeout:         15000,
});

// ─── Request Interceptor: Attach Access Token ─────────────────────────────

axiosInstance.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// ─── Response Interceptor: Global Error Handling + Silent Token Refresh ───

let isRefreshing      = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) {
    refreshSubscribers.push(cb);
}
function onRefreshed(newToken) {
    refreshSubscribers.forEach((cb) => cb(newToken));
    refreshSubscribers = [];
}

axiosInstance.interceptors.response.use(
    (response) => response,

    async (error) => {
        const originalRequest = error.config;
        const status          = error.response?.status;

        // ── 401: Try silent token refresh first ───────────────────────────
        if (status === 401 && !originalRequest._retry) {
            // Avoid refreshing on the auth endpoints themselves
            if (
                originalRequest.url?.includes('/auth/login') ||
                originalRequest.url?.includes('/auth/refresh')
            ) {
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            if (isRefreshing) {
                // Queue the request until refresh resolves
                return new Promise((resolve) => {
                    subscribeTokenRefresh((newToken) => {
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        resolve(axiosInstance(originalRequest));
                    });
                });
            }

            isRefreshing = true;
            try {
                // FIX: Call the new /auth/refresh endpoint
                const res = await axiosInstance.post('/auth/refresh');
                const newToken = res.data?.data?.accessToken;

                if (newToken) {
                    localStorage.setItem('token', newToken);
                    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    onRefreshed(newToken);
                    isRefreshing = false;
                    return axiosInstance(originalRequest);
                }
            } catch (refreshError) {
                // Refresh failed — clear session and redirect to login
                isRefreshing = false;
                localStorage.removeItem('token');
                delete axiosInstance.defaults.headers.common['Authorization'];
                if (!window.location.pathname.includes('/login')) {
                    toast.error('Your session has expired. Please log in again.');
                    window.location.href = '/login';
                }
                return Promise.reject(refreshError);
            }
        }

        // ── 403: Permission denied ─────────────────────────────────────────
        if (status === 403) {
            toast.warn("Access Denied: You don't have permission for this action.");
        }

        // ── 500: Server error ──────────────────────────────────────────────
        if (status === 500) {
            const msg = error.response?.data?.message || 'Server error. Please try again.';
            toast.error(`Server Error: ${msg}`, { autoClose: 6000 });
        }

        // ── 404: Silently log (don't toast for every missing route) ────────
        if (status === 404) {
            console.warn(`API 404: ${originalRequest.method?.toUpperCase()} ${originalRequest.url}`);
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
